import supabase from '../config/supabase';
import config from '../config/environment';
import { PaymentSystem } from '../types';
import { StudentPaymentAccountService } from './PaymentAccountService';
import type {
  StudentSubscriptionPauseRecord,
  StudentSubscriptionTransition,
} from '../types/subscription';

const SYNC_BATCH_SIZE = 90;
const RETRY_BASE_DELAY_MS = 1000;
const RETRY_MAX_DELAY_MS = 8000;
const MAX_429_RETRIES = 5;

const wait = (ms: number) => new Promise<void>((resolve) => {
  setTimeout(resolve, ms);
});

const getRetryAfterMs = (retryAfterHeader: string | null): number | null => {
  if (!retryAfterHeader) {
    return null;
  }

  const asSeconds = Number(retryAfterHeader);
  if (Number.isFinite(asSeconds) && asSeconds >= 0) {
    return asSeconds * 1000;
  }

  const asDate = Date.parse(retryAfterHeader);
  if (!Number.isNaN(asDate)) {
    const delta = asDate - Date.now();
    return Math.max(0, delta);
  }

  return null;
};

const getExponentialBackoffMs = (attempt: number): number => {
  const delay = RETRY_BASE_DELAY_MS * (2 ** attempt);
  return Math.min(delay, RETRY_MAX_DELAY_MS);
};

// ── Types ────────────────────────────────────────────────────────────────────

export interface ExternalCustomer {
  id: string;
  name: string | null;
  email: string | null;
  document: string | null;
}

export interface ExternalSubscription {
  external_subscription_id: string;
  external_customer_id: string;
  value: number;
  description: string | null;
  status: string;
  dateCreated: string | null;
  nextChargeDate?: string | null;
}

export interface ExternalSubscriptionWithPayments extends ExternalSubscription {
  payments: ExternalPayment[];
}

export interface ExternalPayment {
  id: string;
  value: number;
  status: string;
  dueDate: string | null;
  paymentDate: string | null;
  description: string | null;
  raw: Record<string, unknown>;
}

export interface ExternalCustomerSyncGroup {
  customer: ExternalCustomer;
  subscriptions: ExternalSubscriptionWithPayments[];
  standalonePayments: ExternalPayment[];
}

export interface LinkedPaymentAccountJunction {
  student_id: number;
  payment_account_id: number;
  payment_accounts?: {
    external_customer_id?: string | null;
    payment_system?: string | null;
  };
}

export interface ExternalCustomerSubscriptionLookup {
  customer: ExternalCustomer;
  subscriptions: ExternalSubscription[];
}

export interface FixExternalSubscriptionResult {
  student_subscription_id: number;
  previous_external_subscription_id: string | null;
  external_subscription_id: string;
}

export interface SyncPreviewItem {
  studentId: number;
  customers: ExternalCustomerSyncGroup[];
}

interface LegacySyncPreviewItem {
  studentId: number;
  customer: ExternalCustomer | null;
  payments: ExternalPayment[];
}

export interface CreateSubscriptionResult {
  id: string | null;
  url?: string | null;
  student_subscription_id?: number | null;
}

export interface CreateOneTimePaymentResult {
  id: string;
  url?: string | null;
}

export interface PaymentLinkResult {
  url: string | null;
  payload: Record<string, unknown> | null;
}

export interface PaymentDetailResult extends PaymentLinkResult {
  approval_link_refreshed?: boolean;
  external_payment_id?: string;
}

export interface PortalPaymentAccountSearchResult {
  id: number;
  payment_system: PaymentSystem;
  external_customer_id: string;
  name: string | null;
  email: string | null;
  created_at: string;
  linked_students: Array<{ id: number; full_name: string | null }>;
  already_linked_to_student: boolean;
  shares_active_subscription: boolean;
}

export interface LinkStudentPaymentAccountResult {
  student_payment_account_id: number;
  payment_account_id: number;
  already_linked?: boolean;
  previous_student_payment_account_id?: number;
  student_subscription_id?: number;
  subscription_mirrored?: boolean;
  payments_mirrored?: number;
}

export interface DisableAsaasNotificationsResult {
  offset: number;
  processed: number;
  updated: number;
  skipped: number;
  failed: number;
  total: number;
  hasMore: boolean;
  nextOffset: number;
  errors: Array<{ customerId: string; message: string }>;
}

export interface SchedulePlanTransitionResult {
  transition: StudentSubscriptionTransition;
}

// ── Gateway caller ───────────────────────────────────────────────────────────

const normalizeSyncPreviewItem = (
  item: SyncPreviewItem | LegacySyncPreviewItem | null | undefined,
): SyncPreviewItem => {
  const studentId = Number(item?.studentId ?? 0);

  if (!item) {
    return { studentId, customers: [] };
  }

  if (Array.isArray((item as SyncPreviewItem).customers)) {
    return {
      studentId,
      customers: ((item as SyncPreviewItem).customers ?? []).map((group) => ({
        customer: group.customer,
        subscriptions: (group.subscriptions ?? []).map((subscription) => ({
          ...subscription,
          payments: subscription.payments ?? [],
        })),
        standalonePayments: group.standalonePayments ?? [],
      })),
    };
  }

  const legacyItem = item as LegacySyncPreviewItem;
  if (!legacyItem.customer) {
    return { studentId, customers: [] };
  }

  return {
    studentId,
    customers: [{
      customer: legacyItem.customer,
      subscriptions: [],
      standalonePayments: legacyItem.payments ?? [],
    }],
  };
};

const normalizeSyncPreviewItems = (
  items: Array<SyncPreviewItem | LegacySyncPreviewItem> | null | undefined,
): SyncPreviewItem[] => (items ?? []).map(normalizeSyncPreviewItem);

async function callGateway<T>(
  action: string,
  provider: PaymentSystem | null,
  data: Record<string, unknown> = {},
  signal?: AbortSignal,
): Promise<T> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;

  for (let attempt = 0; attempt <= MAX_429_RETRIES; attempt += 1) {
    const res = await fetch(`${config.supabaseFunctionsUrl}/payment-gateway`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
      body: JSON.stringify({
        action,
        ...(provider ? { provider } : {}),
        ...data,
      }),
      signal,
    });

    if (provider === 'asaas') {
      const rateLimitLimit = res.headers.get('RateLimit-Limit');
      const rateLimitRemaining = res.headers.get('RateLimit-Remaining');
      const rateLimitReset = res.headers.get('RateLimit-Reset');
      if (rateLimitLimit || rateLimitRemaining || rateLimitReset) {
        console.info('[GatewaySync][Asaas RateLimit]', {
          action,
          limit: rateLimitLimit,
          remaining: rateLimitRemaining,
          reset: rateLimitReset,
        });
      }
    }

    if (res.status === 429) {
      if (attempt >= MAX_429_RETRIES) {
        throw new Error('Rate limit excedido no gateway (HTTP 429). Tente novamente em instantes.');
      }

      const retryAfterHeader = res.headers.get('Retry-After');
      const retryAfterMs = getRetryAfterMs(retryAfterHeader);
      const backoffMs = getExponentialBackoffMs(attempt);
      const delayMs = Math.max(retryAfterMs ?? 0, backoffMs);

      await wait(delayMs);
      continue;
    }

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.error || 'Payment gateway request failed');
    }

    return res.json();
  }

  throw new Error('Falha ao chamar gateway.');
}

// ── Service ──────────────────────────────────────────────────────────────────

class PaymentGatewayServiceClass {
  async createPixAccount(studentId: number, planId: number): Promise<void> {
    await callGateway('create-pix-account', null, {
      student_id: studentId,
      plan_id: planId,
    });
  }

  async registerPixCharge(
    studentSubscriptionId: number,
    status: 'PAID' | 'PENDING' | 'EXPIRED' | 'CANCELED',
    chargeDate: string,
  ): Promise<void> {
    await callGateway('register-pix-charge', null, {
      student_subscription_id: studentSubscriptionId,
      status,
      charge_date: chargeDate,
    });
  }

  async deletePixCharge(paymentHistoryId: number): Promise<void> {
    await callGateway('delete-pix-charge', null, {
      payment_history_id: paymentHistoryId,
    });
  }

  async cancelPixSubscription(
    studentSubscriptionId: number,
    cancellationReason?: string,
    cancellationObservation?: string | null,
  ): Promise<void> {
    const observation = cancellationObservation?.trim() || null;
    await callGateway('cancel-pix-subscription', null, {
      student_subscription_id: studentSubscriptionId,
      ...(cancellationReason ? { cancellation_reason: cancellationReason } : {}),
      ...(observation ? { cancellation_observation: observation } : {}),
    });
  }

  async createCustomer(provider: PaymentSystem, studentId: number): Promise<ExternalCustomer> {
    const response = await callGateway<{ data: ExternalCustomer }>(
      'create-customer',
      provider,
      { student_id: studentId },
    );
    return response.data;
  }

  async searchCustomers(provider: PaymentSystem, query: string): Promise<ExternalCustomer[]> {
    const response = await callGateway<{ data: ExternalCustomer[] }>(
      'search-customers',
      provider,
      { query },
    );
    return response.data;
  }

  async createSubscription(
    provider: PaymentSystem,
    externalCustomerId: string,
    planId: number,
    studentPaymentAccountId?: number,
    origin: 'portal' | 'app' | 'checkout_screen' = 'portal',
    studentId?: number,
    paymentMethod?: 'pix' | 'credit_card' | null,
  ): Promise<CreateSubscriptionResult> {
    const response = await callGateway<{ data: CreateSubscriptionResult }>(
      'create-subscription',
      provider,
      {
        ...(externalCustomerId ? { external_customer_id: externalCustomerId } : {}),
        plan_id: planId,
        origin,
        ...(studentPaymentAccountId ? { student_payment_account_id: studentPaymentAccountId } : {}),
        ...(studentId ? { student_id: studentId } : {}),
        ...(paymentMethod ? { payment_method: paymentMethod } : {}),
      },
    );
    return response.data;
  }

  async createOneTimePayment(
    provider: PaymentSystem,
    externalCustomerId: string,
    amount: number,
    paymentAccountId: number,
    studentSubscriptionId: number | null,
    description: string,
  ): Promise<CreateOneTimePaymentResult> {
    const response = await callGateway<{ data: CreateOneTimePaymentResult }>(
      'create-one-time-payment',
      provider,
      {
        external_customer_id: externalCustomerId,
        amount,
        payment_account_id: paymentAccountId,
        student_subscription_id: studentSubscriptionId,
        description,
      },
    );

    return response.data;
  }

  async createSubscriptionCharge(
    provider: PaymentSystem,
    paymentAccountId: number,
    studentSubscriptionId: number,
  ): Promise<CreateOneTimePaymentResult> {
    const response = await callGateway<{ data: CreateOneTimePaymentResult }>(
      'create-subscription-charge',
      provider,
      {
        payment_account_id: paymentAccountId,
        student_subscription_id: studentSubscriptionId,
      },
    );

    return response.data;
  }

  async cancelSubscription(
    studentSubscriptionId: number,
    paymentSystem: PaymentSystem,
    externalSubscriptionId: string,
    cancellationReason?: string,
    cancellationObservation?: string | null,
  ): Promise<void> {
    const observation = cancellationObservation?.trim() || null;
    await callGateway(
      'cancel-subscription',
      paymentSystem,
      {
        student_subscription_id: studentSubscriptionId,
        external_subscription_id: externalSubscriptionId,
        ...(cancellationReason ? { cancellation_reason: cancellationReason } : {}),
        ...(observation ? { cancellation_observation: observation } : {}),
      },
    );
  }

  async pauseSubscription(
    provider: PaymentSystem,
    studentSubscriptionId: number,
    pausedUntil: string,
    reason?: string | null,
  ): Promise<StudentSubscriptionPauseRecord> {
    const response = await callGateway<{ data: StudentSubscriptionPauseRecord }>(
      'pause-subscription',
      provider,
      {
        student_subscription_id: studentSubscriptionId,
        paused_until: pausedUntil,
        ...(reason ? { reason } : {}),
      },
    );

    if (!response.data) {
      throw new Error('Resposta inválida ao pausar a assinatura.');
    }

    return response.data;
  }

  async resumeSubscription(
    provider: PaymentSystem,
    studentSubscriptionId: number,
  ): Promise<StudentSubscriptionPauseRecord> {
    const response = await callGateway<{ data: StudentSubscriptionPauseRecord }>(
      'resume-subscription',
      provider,
      { student_subscription_id: studentSubscriptionId },
    );

    if (!response.data) {
      throw new Error('Resposta inválida ao retomar a assinatura.');
    }

    return response.data;
  }

  async cancelPayment(
    paymentSystem: PaymentSystem,
    externalPaymentId: string,
  ): Promise<void> {
    await callGateway(
      'cancel-payment',
      paymentSystem,
      { external_payment_id: externalPaymentId },
    );
  }

  async confirmPortalPayment(
    paymentHistoryId: number,
    paidAtIso: string,
  ): Promise<{ payment_history_id: number; status: string; paid_at: string }> {
    const response = await callGateway<{ data: { payment_history_id: number; status: string; paid_at: string } }>(
      'confirm-portal-payment',
      'asaas',
      {
        payment_history_id: paymentHistoryId,
        paid_at: paidAtIso,
      },
    );

    if (!response.data) {
      throw new Error('Resposta inválida ao confirmar pagamento.');
    }

    return response.data;
  }

  async refundPayment(
    paymentSystem: PaymentSystem,
    externalPaymentId: string,
    paymentHistoryId?: number,
  ): Promise<void> {
    await callGateway(
      'refund-payment',
      paymentSystem,
      {
        external_payment_id: externalPaymentId,
        ...(paymentHistoryId ? { payment_history_id: paymentHistoryId } : {}),
      },
    );
  }

  async getPaymentLink(
    paymentSystem: PaymentSystem,
    externalPaymentId: string,
  ): Promise<PaymentLinkResult> {
    const response = await callGateway<{ data: PaymentLinkResult }>(
      'get-payment-link',
      paymentSystem,
      { external_payment_id: externalPaymentId },
    );
    return response.data;
  }

  async getPaymentDetail(
    paymentSystem: PaymentSystem,
    externalPaymentId: string,
    paymentHistoryId?: number,
    externalSubscriptionId?: string | null,
    recreateApprovalIfExpired = false,
  ): Promise<PaymentDetailResult> {
    const response = await callGateway<{ data?: PaymentDetailResult } & Partial<PaymentDetailResult>>(
      'get-payment-detail',
      paymentSystem,
      {
        external_payment_id: externalPaymentId,
        payment_history_id: paymentHistoryId,
        ...(externalSubscriptionId ? { external_subscription_id: externalSubscriptionId } : {}),
        ...(recreateApprovalIfExpired ? { recreate_approval_if_expired: true } : {}),
      },
    );

    const normalizeDetail = (detail: Partial<PaymentDetailResult> | null | undefined): PaymentDetailResult => ({
      url: typeof detail?.url === 'string' ? detail.url : null,
      payload: detail?.payload && typeof detail.payload === 'object' && !Array.isArray(detail.payload)
        ? detail.payload as Record<string, unknown>
        : null,
      approval_link_refreshed: detail?.approval_link_refreshed === true,
      external_payment_id: typeof detail?.external_payment_id === 'string'
        ? detail.external_payment_id
        : undefined,
    });

    if (response && typeof response === 'object' && 'payload' in response) {
      return normalizeDetail(response);
    }

    return normalizeDetail(response.data);
  }

  async getExternalSubscriptions(
    paymentSystem: PaymentSystem,
    externalSubscriptionId: string,
    signal?: AbortSignal,
  ): Promise<ExternalSubscription[]> {
    const response = await callGateway<{ data: ExternalSubscription[] }>(
      'get-subscription',
      paymentSystem,
      { external_subscription_id: externalSubscriptionId },
      signal,
    );
    return response.data ?? [];
  }

  async listLinkedPaymentAccountsByProvider(
    provider: PaymentSystem,
    studentIds: number[],
  ): Promise<LinkedPaymentAccountJunction[]> {
    try {
      const response = await callGateway<{ data: LinkedPaymentAccountJunction[] }>(
        'list-linked-payment-accounts-by-provider',
        provider,
        studentIds.length > 0 ? { student_ids: studentIds } : {},
      );
      return response.data ?? [];
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      const isUnsupportedAction = message.includes('Invalid action')
        || message.includes('Unknown action');
      if (!isUnsupportedAction || studentIds.length === 0) {
        throw error;
      }

      const linked: LinkedPaymentAccountJunction[] = [];
      const chunkSize = 20;
      for (let index = 0; index < studentIds.length; index += chunkSize) {
        const chunk = studentIds.slice(index, index + chunkSize);
        const chunkRows = await Promise.all(
          chunk.map((studentId) => StudentPaymentAccountService.findAllByStudentIdWithAccount(studentId)),
        );

        chunk.forEach((studentId, chunkIndex) => {
          for (const row of chunkRows[chunkIndex] ?? []) {
            const paymentAccount = row.payment_accounts;
            if (paymentAccount?.payment_system !== provider || !paymentAccount.external_customer_id) {
              continue;
            }

            linked.push({
              student_id: studentId,
              payment_account_id: row.payment_account_id,
              payment_accounts: {
                external_customer_id: paymentAccount.external_customer_id,
                payment_system: paymentAccount.payment_system,
              },
            });
          }
        });
      }

      return linked;
    }
  }

  async syncPreview(
    provider: PaymentSystem,
    students: Array<{ studentId: number; email: string }>,
    signal?: AbortSignal,
  ): Promise<SyncPreviewItem[]> {
    if (students.length > SYNC_BATCH_SIZE) {
      throw new Error(`O preview aceita no máximo ${SYNC_BATCH_SIZE} alunas por requisição.`);
    }

    const response = await callGateway<{ data: Array<SyncPreviewItem | LegacySyncPreviewItem> }>(
      'sync-preview',
      provider,
      { students },
      signal,
    );
    return normalizeSyncPreviewItems(response.data);
  }

  async syncCommit(
    provider: PaymentSystem,
    items: Array<{
      studentId: number;
      externalCustomerId: string;
      customerName: string | null;
      customerEmail: string | null;
      existingPaymentAccountId: number | null;
      payments: Array<{
        id: string;
        value: number;
        status: string;
        dueDate: string | null;
        paymentDate: string | null;
        raw: Record<string, unknown>;
      }>;
    }>,
  ): Promise<{ synced: number; errors: number; errorDetails: Array<{ studentId: number; externalCustomerId: string | null; message: string }> }> {
    if (items.length > SYNC_BATCH_SIZE) {
      throw new Error(`A sincronização aceita no máximo ${SYNC_BATCH_SIZE} alunas por requisição.`);
    }

    const response = await callGateway<{ data: { synced: number; errors: number; errorDetails: Array<{ studentId: number; externalCustomerId: string | null; message: string }> } }>(
      'sync-commit',
      provider,
      { items },
    );
    return {
      synced: response.data?.synced ?? 0,
      errors: response.data?.errors ?? 0,
      errorDetails: (response.data?.errorDetails ?? []).map((detail) => ({
        studentId: detail.studentId,
        externalCustomerId: detail.externalCustomerId ?? null,
        message: detail.message,
      })),
    };
  }

  async lookupCustomerSubscriptions(
    provider: PaymentSystem,
    email: string,
    signal?: AbortSignal,
  ): Promise<ExternalCustomerSubscriptionLookup[]> {
    const response = await callGateway<{ data: ExternalCustomerSubscriptionLookup[] }>(
      'lookup-customer-subscriptions',
      provider,
      { email },
      signal,
    );

    return response.data ?? [];
  }

  async lookupStudentSubscriptionCandidates(
    provider: PaymentSystem,
    studentSubscriptionId: number,
    signal?: AbortSignal,
  ): Promise<ExternalCustomerSubscriptionLookup[]> {
    const response = await callGateway<{ data: ExternalCustomerSubscriptionLookup[] }>(
      'lookup-student-subscription-candidates',
      provider,
      { student_subscription_id: studentSubscriptionId },
      signal,
    );

    return response.data ?? [];
  }

  async fixExternalSubscriptionId(
    provider: PaymentSystem,
    studentSubscriptionId: number,
    externalSubscriptionId: string,
    signal?: AbortSignal,
  ): Promise<FixExternalSubscriptionResult> {
    const response = await callGateway<{ data: FixExternalSubscriptionResult }>(
      'fix-external-subscription-id',
      provider,
      {
        student_subscription_id: studentSubscriptionId,
        external_subscription_id: externalSubscriptionId,
      },
      signal,
    );

    if (!response.data) {
      throw new Error('Resposta inválida ao corrigir assinatura.');
    }

    return response.data;
  }

  async searchPortalPaymentAccounts(
    studentId: number,
    query?: string,
    paymentSystem?: PaymentSystem,
  ): Promise<PortalPaymentAccountSearchResult[]> {
    const response = await callGateway<{ data: PortalPaymentAccountSearchResult[] }>(
      'search-portal-payment-accounts',
      'asaas',
      {
        student_id: studentId,
        ...(query ? { query } : {}),
        ...(paymentSystem ? { payment_system: paymentSystem } : {}),
      },
    );

    return response.data ?? [];
  }

  async linkStudentPaymentAccount(
    studentId: number,
    paymentAccountId: number,
  ): Promise<LinkStudentPaymentAccountResult> {
    const response = await callGateway<{ data: LinkStudentPaymentAccountResult }>(
      'link-student-payment-account',
      'asaas',
      {
        student_id: studentId,
        payment_account_id: paymentAccountId,
      },
    );

    if (!response.data) {
      throw new Error('Resposta inválida ao vincular conta de pagamento.');
    }

    return response.data;
  }

  async replaceStudentPaymentAccount(
    studentId: number,
    currentStudentPaymentAccountId: number,
    paymentAccountId: number,
  ): Promise<LinkStudentPaymentAccountResult> {
    const response = await callGateway<{ data: LinkStudentPaymentAccountResult }>(
      'replace-student-payment-account',
      'asaas',
      {
        student_id: studentId,
        current_student_payment_account_id: currentStudentPaymentAccountId,
        payment_account_id: paymentAccountId,
      },
    );

    if (!response.data) {
      throw new Error('Resposta inválida ao alterar conta de pagamento.');
    }

    return response.data;
  }

  async disableAsaasNotifications(
    studentIds: number[],
    offset = 0,
    limit = 50,
  ): Promise<DisableAsaasNotificationsResult> {
    const response = await callGateway<{ data: DisableAsaasNotificationsResult }>(
      'disable-asaas-notifications',
      'asaas',
      { student_ids: studentIds, offset, limit },
    );

    if (!response.data) {
      throw new Error('Resposta inválida ao desativar notificações do Asaas.');
    }

    return response.data;
  }

  async schedulePlanTransition(
    provider: PaymentSystem,
    studentSubscriptionId: number,
    nextPlanId: number,
    effectiveStartDate: string,
    options?: {
      nextGatewayProvider?: PaymentSystem;
      nextPaymentMethod?: 'pix' | 'credit_card' | null;
    },
  ): Promise<SchedulePlanTransitionResult> {
    const response = await callGateway<{ data: SchedulePlanTransitionResult }>(
      'schedule-plan-transition',
      provider,
      {
        student_subscription_id: studentSubscriptionId,
        next_plan_id: nextPlanId,
        effective_start_date: effectiveStartDate,
        ...(options?.nextGatewayProvider
          ? { next_gateway_provider: options.nextGatewayProvider }
          : {}),
        ...(options?.nextPaymentMethod
          ? { next_payment_method: options.nextPaymentMethod }
          : {}),
      },
    );

    if (!response.data?.transition) {
      throw new Error('Resposta inválida ao agendar troca de plano.');
    }

    return response.data;
  }

  async getScheduledPlanTransition(
    provider: PaymentSystem,
    studentSubscriptionId: number,
  ): Promise<StudentSubscriptionTransition | null> {
    const response = await callGateway<{ data: StudentSubscriptionTransition | null }>(
      'get-scheduled-plan-transition',
      provider,
      { student_subscription_id: studentSubscriptionId },
    );

    return response.data ?? null;
  }

  async cancelScheduledPlanTransition(
    provider: PaymentSystem,
    studentSubscriptionId: number,
  ): Promise<StudentSubscriptionTransition> {
    const response = await callGateway<{ data: SchedulePlanTransitionResult }>(
      'cancel-scheduled-plan-transition',
      provider,
      { student_subscription_id: studentSubscriptionId },
    );

    if (!response.data?.transition) {
      throw new Error('Resposta inválida ao cancelar a troca de plano.');
    }

    return response.data.transition;
  }
}

export const PaymentGatewayService = new PaymentGatewayServiceClass();
